import React from "react";
import type { ModelGradeType, PricePromotionType } from "../../../../../../common-deprecated/types/CommonTypes";
import { getGrLogo } from "../../../../../../common-deprecated/utils";
import { getEcoBonusPromotions } from "../../../../../../common-deprecated/utils/promotionUtils";
import { type BuildAndBuyDispatchType, useBnbSelector } from "../../../redux/store";
import { useCarImage } from "../../../hooks/gradeEngine/useCarImage";
import { useBnbLabel, useBnbOptionalLabel } from "../../../utils/labels";
import HorizontalEcoTag from "../common/HorizontalEcoTag";
import GRLogo from "../../../../../../../assets/toyota/svg/GR/TME-GR.svg";
import GRLogoSport from "../../../../../../../assets/toyota/svg/GR/TME-GR-sport-black.svg";
import * as Styles from "./styles/GradeBlock";
import ImageFluid from "../../../../../../common-deprecated/styles/v2/toyota/globals/Image";
import { GrCode } from "../../../../../../common-deprecated/constants";
import { showModal } from "../../../../../../common-deprecated/redux/actions/ModalActions";
import { MODAL_CONTENT } from "../../../../../../common-deprecated/utils/modalConstants";
import { IconWrapper } from "../../../../../../common-deprecated/styles/v2/toyota/globals/IconWrapper";
import Icon from "../../../../../../common-deprecated/styles/v2/toyota/globals/Icon";
import EcoBonusPromotion from "../../../../../../common-deprecated/components/aem/EcoBonusPromotion";
import { GradeEquipmentList } from "./GradeEquipmentList";
import DisplayPrice from "./price/Price";
import { MODAL_GRADE_INTERIOR } from "../../../utils/modalConstants";
import GradeEnginesOverview from "./GradeEnginesOverview";
import ButtonTertiary from "../../../../../../common-deprecated/components/aem/toyota/ButtonTertiary";
import * as Tooltip from "../../../../../../common-deprecated/styles/v2/toyota/components/Tooltip";
import { isRTL } from "../../../../../../common-deprecated/settings/utils/commonSettingUtils";
import { NavStep } from "../../../../shared-logic/utils/Navigation";
import { trackShowInterior } from "../../../utils/tracking";
import { useDispatch } from "../../../../../../shared-logic/redux/store";

type GradeBlockProps = {
    grade: ModelGradeType;
    isSelected: boolean;
    setSelected: (gradeId: string) => void;
};

export const GradeBlock = ({ grade, isSelected, setSelected }: GradeBlockProps): JSX.Element => {
    const dispatch = useDispatch<BuildAndBuyDispatchType>();
    const model = useBnbSelector((state) => state.model);
    const commonSettings = useBnbSelector((state) => state.commonSettings);
    // const uniqueId = useUniqueId(); NEEDED LATER FOR NAVIGATION
    const { getImage } = useCarImage();

    const { ecoTag } = grade;
    const ecoBonus = useBnbSelector((state) =>
        getEcoBonusPromotions<PricePromotionType>(state.items.promotions, grade.price.promotions),
    );
    const grLogoCheck = getGrLogo(model.name, grade.name);
    const [noAccesoriesDisclaimer, genericDisclaimerTitle] = useBnbOptionalLabel([
        "genericModelDisclaimer",
        "genericDisclaimerTitle",
    ]);
    const showInteriorLabel = useBnbLabel("showInterior");

    // NEEDED LATER FOR COLLAPSABLE LOGIC
    const carImageProps = {
        modelId: model.id,
        gradeId: grade.id,
        bodyTypeId: grade.bodyType.id,
        defaultCarId: model.defaultCarId,
    };

    return (
        <Styles.Wrapper selected={isSelected}>
            <Styles.Header>
                <Styles.GradeName>{grade.name}</Styles.GradeName>
                <Styles.BodyTypeName>{grade.bodyType.name}</Styles.BodyTypeName>

                <Styles.PriceWrapper>
                    <EcoBonusPromotion ecoBonus={ecoBonus} />
                    <DisplayPrice price={grade.price} financeInfo={grade.finance} cashPriceDisclaimer={model.labels} />
                    {!!noAccesoriesDisclaimer && (
                        <Tooltip.TooltipButton
                            type="button"
                            onClick={() =>
                                dispatch(showModal({ type: MODAL_CONTENT, content: noAccesoriesDisclaimer }))
                            }
                        >
                            {genericDisclaimerTitle}
                            <IconWrapper>
                                <Icon variant="info" />
                            </IconWrapper>
                        </Tooltip.TooltipButton>
                    )}
                </Styles.PriceWrapper>
                {ecoTag && <HorizontalEcoTag type={ecoTag} />}
            </Styles.Header>

            <Styles.Content>
                <GradeEquipmentList grade={grade} model={model} />
            </Styles.Content>

            <Styles.ImageWrapper isRTL={isRTL(commonSettings)}>
                {grLogoCheck && (
                    <Styles.GRLogo>{grLogoCheck === GrCode.Gr ? <GRLogo /> : <GRLogoSport />}</Styles.GRLogo>
                )}
                <ImageFluid
                    src={getImage({ ...carImageProps, width: 464, height: 278 })}
                    srcSet={
                        `${getImage({ ...carImageProps, width: 464, height: 278 })} 1x,` +
                        `${getImage({ ...carImageProps, width: 928, height: 556 })} 2x,`
                    }
                    alt={`${model.name} ${grade.name} ${grade.bodyType.name || ""}`}
                />
                <ButtonTertiary
                    onClick={() => {
                        dispatch(showModal({ type: MODAL_GRADE_INTERIOR, grade }));
                        trackShowInterior(NavStep.ModelEngine, grade.name);
                    }}
                    aria-label={showInteriorLabel}
                >
                    {showInteriorLabel}
                </ButtonTertiary>
            </Styles.ImageWrapper>

            <GradeEnginesOverview
                grade={grade}
                engines={grade.motorizations}
                isSelected={isSelected}
                setSelected={setSelected}
            />
        </Styles.Wrapper>
    );
};
